// Compliance Evaluation Sheet (CES) logic, ported verbatim (rule-engine
// reference §8). Maps a reviewer's per-criterion status to a register level and
// rolls the rows up to an overall transaction level; seeds editable rows from
// the rulebook when a CES is opened for a route.

import { CES_ROUTE_DEFS } from './rulebook/routes';
import type { CesRoute } from './enums';
import type { CesRow } from '../data/types';

// Map a reviewer's per-criterion CES status to a register compliance level. The
// status itself encodes the compliance category (PFMA / Internal Policies /
// Housekeeping / Compliant / Not-Applicable — see CES_STATUS_OPTIONS), so the
// mapping is status-only. (The former `riskText` fallback was dead for the
// canonical status set and is removed with the Risk field — owner feedback 5.)
export function cesMapStatusToRegister(status: string): string {
  const s = String(status || '').toLowerCase();
  if (!s) return 'Pending Review';
  if (s.includes('housekeeping')) return 'Non-Compliance Housekeeping';
  if (s.includes('pfma')) return 'Non-Compliance: Possible IE';
  if (s.includes('internal')) return 'Non-Compliant: Policy & Procedures';
  if (s.includes('not-applicable')) return 'Not Applicable';
  if (s.includes('compliant')) return 'Compliant';
  return 'Pending Review';
}

export interface CesRowStatus {
  status: string;
}

export function cesOverallCompliance(rows: CesRowStatus[], override?: string): string {
  if (override) return override;
  const mapped = rows.map((r) => cesMapStatusToRegister(r.status));
  if (mapped.includes('Non-Compliance: Possible IE')) return 'Non-Compliance: Possible IE';
  if (mapped.includes('Non-Compliant: Policy & Procedures')) return 'Non-Compliant: Policy & Procedures';
  if (mapped.includes('Non-Compliance Housekeeping')) return 'Non-Compliance Housekeeping';
  if (mapped.some((x) => x === 'Pending Review')) return 'Pending Review';
  if (mapped.length && mapped.every((x) => x === 'Compliant' || x === 'Not Applicable')) {
    return 'Compliant';
  }
  return 'Pending Review';
}

/** Seed editable CES rows for a route; the reviewer then fills status/finding. */
export function seedCesRows(route: CesRoute, transactionId: string): CesRow[] {
  const def = CES_ROUTE_DEFS[route];
  return def.criteria.map((c, i) => ({
    transactionId,
    seq: i + 1,
    stage: c.stage,
    criterion: c.criterion,
    status: '',
    finding: '',
  }));
}
